// ./src/pages/private/UserInvitationsPage/UserInvitationsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  PremiumFeatureGate,
  FeatureBenefitsList,
} from "@/components/generic";
import {
  UserInvitationEditModal,
  UserInvitationsTable,
} from "@/components/features";
import { useUserInvitations, useNumericFeature } from "@/hooks";
import { UserInvitation, UserInvitationInput } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { FEATURE_CODES } from "@/config";
import { ENTITIES } from "@/config/userRoles";

const USER_INVITATIONS_BENEFITS = [
  "Invite team members to collaborate on form management",
  "Assign roles with different permission levels",
  "Streamline onboarding with email-based invitations",
  "Track pending and accepted invitations",
  "Resend invitations with a single click",
  "Manage access for agencies and enterprise teams",
];

export const UserInvitationsPage: React.FC<PageProps> = (props: PageProps) => {
  const { hasRightTo, isAuthenticated } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ userInvitationId?: string }>();

  // Check if user invitations feature is enabled (MAX_USERS_QTY > 1 means team features)
  const { maxValue: maxUsers } = useNumericFeature(FEATURE_CODES.MAX_USERS_QTY);

  // Team features enabled if more than 1 user allowed
  const isTeamFeaturesEnabled = maxUsers > 1;

  // Parse URL to determine which modal to show
  const invitationIdFromUrl = params.userInvitationId;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode =
    !!invitationIdFromUrl && location.pathname.endsWith("/edit");

  // Use the user invitations hook for all data management
  const {
    userInvitations,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    isCreating,
    isUpdating,
    removingInvitationId,
    resendingInvitationId,
    list,
    loadMore,
    refresh,
    get,
    create,
    update,
    remove,
    resend,
    lastError,
    clearError,
  } = useUserInvitations({
    autoLoad: isTeamFeaturesEnabled,
    isAuthenticated,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [invitationToDelete, setInvitationToDelete] =
    useState<UserInvitation | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingInvitation, setEditingInvitation] =
    useState<UserInvitation | null>(null);
  const [_, setEditedInvitationId] = useState<string | null>(null);

  const canCreate = hasRightTo(
    ENTITIES.USER_INVITATIONS,
    OPERATION_RIGHTS.CREATE
  );

  // Load invitation when in edit mode
  useEffect(() => {
    if (isEditMode && invitationIdFromUrl && isTeamFeaturesEnabled) {
      const loadInvitation = async () => {
        await get(invitationIdFromUrl);
      };
      loadInvitation();
    }
  }, [isEditMode, invitationIdFromUrl, get, isTeamFeaturesEnabled]);

  // Update editing invitation when we get data
  useEffect(() => {
    if (invitationIdFromUrl) {
      const invitation = userInvitations.find(
        (inv) => inv.id === invitationIdFromUrl
      );
      if (invitation) {
        if (isEditMode) {
          setEditingInvitation(invitation);
        }
      }
    } else {
      setEditingInvitation(null);
    }
  }, [isEditMode, invitationIdFromUrl, userInvitations]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      const newFilters: any = { ...filters };
      if (value.trim()) {
        newFilters.keyword = value.trim();
        newFilters.searchIn = ["firstName", "lastName", "email"];
      } else {
        delete newFilters.keyword;
        delete newFilters.searchIn;
      }

      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, sortBy, list]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSortBy: any[]) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, newSortBy, true);
    },
    [filters, list]
  );

  // Handle add invitation button click
  const handleAddInvitation = useCallback(() => {
    clearError();
    navigate("/account/users/invitations/add");
  }, [navigate, clearError]);

  // Handle edit invitation
  const handleEditInvitation = useCallback(
    (invitation: UserInvitation) => {
      clearError();
      if (invitation.id) {
        navigate(`/account/users/invitations/${invitation.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle remove invitation button click
  const handleRemoveInvitationClick = useCallback(
    (invitation: UserInvitation) => {
      setInvitationToDelete(invitation);
    },
    []
  );

  // Handle resend invitation
  const handleResendInvitation = useCallback(
    async (invitation: UserInvitation) => {
      if (invitation.id) {
        await resend(invitation.id);
      }
    },
    [resend]
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (invitationToDelete?.id) {
      const success = await remove(invitationToDelete.id);
      if (success) {
        setInvitationToDelete(null);
        if (invitationIdFromUrl === invitationToDelete.id) {
          navigate("/account/users/invitations");
        }
      }
    }
  }, [invitationToDelete, remove, invitationIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setInvitationToDelete(null);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditingInvitation(null);
    clearError();
    navigate("/account/users/invitations");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle invitation creation
  const handleCreateInvitation = useCallback(
    async (data: UserInvitationInput): Promise<UserInvitation | null> => {
      const result = await create(data);
      if (result?.id) {
        setEditedInvitationId(result.id);
        navigate("/account/users/invitations");
      }
      return result;
    },
    [create, navigate]
  );

  // Handle invitation update
  const handleUpdateInvitation = useCallback(
    async (
      id: string,
      data: UserInvitationInput
    ): Promise<UserInvitation | null> => {
      const result = await update(id, data);
      if (result) {
        setEditedInvitationId(result.id || null);
        navigate("/account/users/invitations");
      }
      return result;
    },
    [update, navigate]
  );

  return (
    <DashboardLayout {...props}>
      <PremiumFeatureGate
        featureCode={FEATURE_CODES.MAX_USERS_QTY}
        featureTitle="Team Collaboration"
        forceEnabled={isTeamFeaturesEnabled}
        featureDescription={
          <FeatureBenefitsList benefits={USER_INVITATIONS_BENEFITS} />
        }
      >
        <Stack gap="md">
          {/* Page toolbar with actions and search */}
          <AppToolbar
            preset="page-header"
            sections={[
              {
                id: "actions",
                items: [
                  <AppButton
                    key="add-invitation"
                    iconName="Plus"
                    onClick={handleAddInvitation}
                    disabled={
                      isCreating ||
                      !!removingInvitationId ||
                      loading ||
                      !canCreate
                    }
                  >
                    Invite User
                  </AppButton>,
                  <AppButton
                    key="refresh"
                    variant="secondary"
                    iconName="Refresh"
                    onClick={handleRefresh}
                    disabled={isCreating || !!removingInvitationId || loading}
                  />,
                ],
                align: "left",
                gap: "sm",
              },
              {
                id: "search",
                items: [
                  <SearchInput
                    key="search"
                    placeholder="Search invitations..."
                    value={searchValue}
                    onChange={(value) => handleSearchChange(value)}
                    style={{ minWidth: 250 }}
                  />,
                ],
                align: "right",
              },
            ]}
          />

          {/* User invitations table */}
          <UserInvitationsTable
            userInvitations={userInvitations}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onLoadMore={loadMore}
            onEditInvitation={handleEditInvitation}
            onRemoveInvitation={handleRemoveInvitationClick}
            onResendInvitation={handleResendInvitation}
            removingInvitationId={removingInvitationId}
            resendingInvitationId={resendingInvitationId}
            //editedItemId={editedInvitationId}
          />

          {/* Empty state when no invitations */}
          <EmptyState
            display={isEmpty && !loading}
            title="No user invitations yet"
            description="Start by inviting your first team member to collaborate"
            actionIcon="Plus"
            actionLabel="Invite User"
            onAction={handleAddInvitation}
          />
        </Stack>

        {/* User invitation edit modal (drawer) for create/edit */}
        <UserInvitationEditModal
          opened={isAddMode || isEditMode}
          onClose={handleEditModalClose}
          userInvitation={isEditMode ? editingInvitation : null}
          onCreateUserInvitation={handleCreateInvitation}
          onUpdateUserInvitation={handleUpdateInvitation}
          isCreating={isCreating}
          isUpdating={isUpdating}
          lastError={lastError}
          onClearError={clearError}
        />

        {/* Delete confirmation modal */}
        <ConfirmModal
          opened={!!invitationToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete User Invitation"
          message={`Are you sure you want to delete the invitation for "${invitationToDelete?.email}"?`}
          details="This action cannot be undone. The invitation will be permanently removed and the user will not be able to accept it."
          confirmInProgress={removingInvitationId === invitationToDelete?.id}
          confirmLabel="Delete"
          type="danger"
        />
      </PremiumFeatureGate>
    </DashboardLayout>
  );
};
